/**
 * OneGrid — 클립보드(복사·잘라내기·붙여넣기)·채우기 핸들
 *
 * OneGrid 메서드 묶음 (믹스인). grid.ts 의 applyMixins 가 이 클래스의 메서드를 OneGrid.prototype 에 붙이고,
 * `interface OneGrid extends ...` 로 타입을 합친다. 메서드마다 첫 매개변수 `this: OneGrid` — 본문은 grid.ts 클래스 안에
 * 있을 때와 같다(this 로 그리드 상태·다른 메서드를 그대로 씀). 직접 new 하지 않음. 예전 private 은 JSDoc internal 태그 (d.ts 에서 뺌)
 */
import { parseTsv, tsvField } from './tsv.js';
import { fillSeries } from './editors.js';
import { NIR, CHECKISH } from './grid-util.js';
export class GridClipboard {
    /** @internal */ startFill(e) {
        if (e.button !== 0)
            return;
        e.preventDefault();
        e.stopPropagation();
        const src = this.range();
        this.anchor = { vi: src.r0, ci: src.c0 };
        this.focus = { vi: src.r1, ci: src.c1 };
        let dir = null, to = 0;
        const move = (ev) => {
            const h = this.hit(ev);
            if (!h)
                return;
            // 범위 밖으로 가장 많이 벗어난 방향 (위·왼쪽은 범위 시작 기준)
            // 멀티로우는 위·아래로만 (컬럼 번호가 화면 가로 순서가 아님)
            const dd = h.vi - src.r1, du = src.r0 - h.vi, dr = this.navOrd ? -1 : h.ci - src.c1, dl = this.navOrd ? -1 : src.c0 - h.ci;
            const m = Math.max(dd, dr, du, dl);
            if (m <= 0)
                dir = null;
            else if (dd === m) {
                dir = 'down';
                to = h.vi;
            }
            else if (du === m) {
                dir = 'up';
                to = h.vi;
            }
            else if (dr === m) {
                dir = 'right';
                to = h.ci;
            }
            else {
                dir = 'left';
                to = Math.max(0, h.ci);
            }
            if (dir === 'up' || dir === 'left') {
                this.anchor = { vi: src.r1, ci: src.c1 };
                this.setFocus(dir === 'up' ? to : src.r0, dir === 'left' ? to : src.c0, true);
            }
            else {
                this.anchor = { vi: src.r0, ci: src.c0 };
                this.setFocus(dir === 'down' ? to : src.r1, dir === 'right' ? to : src.c1, true);
            }
        };
        const up = () => {
            window.removeEventListener('mousemove', move, true);
            window.removeEventListener('mouseup', up, true);
            if (!dir) {
                this.anchor = { vi: src.r0, ci: src.c0 };
                this.setFocus(src.r1, src.c1, true);
                return;
            }
            this.fillRange(src, dir, to);
        };
        window.addEventListener('mousemove', move, true);
        window.addEventListener('mouseup', up, true);
    }
    /** 선택 영역 src 를 dir 방향으로 to(행 vi 또는 컬럼 ci)까지 채움. 코드에서도 호출 가능 */
    fillRange(src, dir, to) {
        const changes = [];
        const ok = (c, row) => !!c && !c.special && row >= 0 && !!this.editorOf(c, row) && this.canEditCol(c, row);
        if (dir === 'up') {
            // 위로: 원본을 거꾸로 이어 감 (1,2,3 → 위는 0,-1…), 대상도 원본에서 멀어지는 순서
            const tg = [];
            for (let vi = src.r0 - 1; vi >= to && vi >= 0; vi--)
                if (this.disp[vi] >= 0)
                    tg.push(this.disp[vi]);
            for (let ci = src.c0; ci <= src.c1; ci++) {
                const c = this.cols[ci];
                if (!c || c.special)
                    continue;
                const vals = [];
                for (let vi = src.r1; vi >= src.r0; vi--)
                    if (this.disp[vi] >= 0)
                        vals.push(this.ds.get(this.disp[vi], c.id));
                fillSeries(vals, tg.length).forEach((v, k) => { if (ok(c, tg[k]))
                    changes.push({ row: tg[k], col: c.id, value: v }); });
            }
        }
        else if (dir === 'left') {
            const tg = [];
            for (let ci = src.c0 - 1; ci >= to && ci >= 0; ci--)
                if (!this.cols[ci].special)
                    tg.push(this.cols[ci]);
            for (let vi = src.r0; vi <= src.r1; vi++) {
                const row = this.disp[vi];
                if (row < 0)
                    continue;
                const vals = [];
                for (let ci = src.c1; ci >= src.c0; ci--)
                    if (!this.cols[ci].special)
                        vals.push(this.ds.get(row, this.cols[ci].id));
                fillSeries(vals, tg.length).forEach((v, k) => { if (ok(tg[k], row))
                    changes.push({ row, col: tg[k].id, value: v }); });
            }
        }
        else if (dir === 'down') {
            const tg = [];
            for (let vi = src.r1 + 1; vi <= to && vi < this.disp.length; vi++)
                if (this.disp[vi] >= 0)
                    tg.push(this.disp[vi]);
            for (let ci = src.c0; ci <= src.c1; ci++) {
                const c = this.cols[ci];
                if (!c || c.special)
                    continue;
                const vals = [];
                for (let vi = src.r0; vi <= src.r1; vi++)
                    if (this.disp[vi] >= 0)
                        vals.push(this.ds.get(this.disp[vi], c.id));
                fillSeries(vals, tg.length).forEach((v, k) => { if (ok(c, tg[k]))
                    changes.push({ row: tg[k], col: c.id, value: v }); });
            }
        }
        else {
            const tg = [];
            for (let ci = src.c1 + 1; ci <= to && ci < this.cols.length; ci++)
                if (!this.cols[ci].special)
                    tg.push(this.cols[ci]);
            for (let vi = src.r0; vi <= src.r1; vi++) {
                const row = this.disp[vi];
                if (row < 0)
                    continue;
                const vals = [];
                for (let ci = src.c0; ci <= src.c1; ci++)
                    if (!this.cols[ci].special)
                        vals.push(this.ds.get(row, this.cols[ci].id));
                fillSeries(vals, tg.length).forEach((v, k) => { if (ok(tg[k], row))
                    changes.push({ row, col: tg[k].id, value: v }); });
            }
        }
        if (changes.length)
            this.setCellByUser(changes, this.t('채우기'), 'fill');
        // 값 변경 뒤 보기 갱신이 선택을 접으므로 마지막에
        if (dir === 'up' || dir === 'left') {
            this.anchor = { vi: src.r1, ci: src.c1 };
            this.setFocus(dir === 'up' ? Math.max(0, to) : src.r0, dir === 'left' ? Math.max(0, to) : src.c0, true);
        }
        else {
            this.anchor = { vi: src.r0, ci: src.c0 };
            this.setFocus(dir === 'down' ? Math.min(to, this.disp.length - 1) : src.r1, dir === 'right' ? Math.min(to, this.cols.length - 1) : src.c1, true);
        }
        return changes.length;
    }
    /** @internal */ onCopy(e) {
        if (this.editing)
            return;
        e.clipboardData?.setData('text/plain', this.getSelectionText(this.copyHeaders || !!this.opts.copyWithHeaders));
        this.copyHeaders = false;
        e.preventDefault();
    }
    /** 잘라내기 (Ctrl+X): 복사 후 편집 가능한 칸만 지움 (읽기 전용·권한 read 칸은 남김) */
    /** @internal */ onCut(e) {
        if (this.editing)
            return;
        this.onCopy(e);
        if (this.opts.editable)
            this.clearRange();
    }
    /**
     * 선택 영역 → TSV(엑셀 규칙: 탭·줄바꿈·앞 " 가 있는 값은 "…" 로 감쌈). 여러 범위: 같은 컬럼들이면 행을 이어 붙이고, 같은 행들이면 컬럼을 옆으로 붙이고, 아니면 범위마다 빈 줄로 구분.
     * withHeaders: 첫 줄에 머리글
     */
    getSelectionText(withHeaders = false) {
        const rs = this.selRanges().map(r => ({ ...r })).sort((a, b) => a.r0 - b.r0 || a.c0 - b.c0);
        const cell = (c, row) => { const v = this.valueOf(c, row); return typeof v === 'number' && !c.def.items ? String(v) : tsvField(this.displayText(c, v, row)); };
        const colsOf = (r) => this.cols.slice(r.c0, r.c1 + 1).filter(c => !c.special);
        const rowsOf = (r) => { const out = []; for (let vi = r.r0; vi <= r.r1; vi++) {
            const row = this.disp[vi];
            if (row != null && row >= 0)
                out.push(row);
        } return out; };
        const head = (cs) => cs.map(c => tsvField(String(c.def.header ?? c.id))).join('\t');
        const block = (r, hdr) => { const cs = colsOf(r); return [...(hdr ? [head(cs)] : []), ...rowsOf(r).map(row => cs.map(c => cell(c, row)).join('\t'))]; };
        if (rs.length === 1)
            return block(rs[0], withHeaders).join('\r\n');
        const sameCols = rs.every(r => r.c0 === rs[0].c0 && r.c1 === rs[0].c1);
        const byCol = [...rs].sort((a, b) => a.c0 - b.c0);
        const sameRows = rs.every(r => r.r0 === rs[0].r0 && r.r1 === rs[0].r1);
        if (sameCols)
            return [...(withHeaders ? [head(colsOf(rs[0]))] : []), ...rs.flatMap(r => block(r, false))].join('\r\n');
        if (sameRows) {
            const cs = byCol.flatMap(colsOf), rows = rowsOf(rs[0]);
            return [...(withHeaders ? [head(cs)] : []), ...rows.map(row => cs.map(c => cell(c, row)).join('\t'))].join('\r\n');
        }
        return rs.map(r => block(r, withHeaders).join('\r\n')).join('\r\n\r\n');
    }
    /** 머리글 포함 복사 (Ctrl+Shift+C) */
    copyWithHeaders() { this.copyHeaders = true; this.ime.focus(); document.execCommand('copy'); }
    /** @internal */ onPaste(e) {
        if (this.editing)
            return;
        const text = e.clipboardData?.getData('text/plain');
        if (!text || !this.opts.editable)
            return;
        e.preventDefault();
        let rows = parseTsv(text); // 엑셀 따옴표 규칙: "…" 안의 탭·줄바꿈은 값
        const start = { ...this.focus };
        const nirRow = start.vi === NIR ? this.ensureNirRow() : -1;
        if (start.vi === NIR) {
            if (nirRow < 0)
                return;
            rows = rows.slice(0, 1);
        }
        let errors = 0;
        const changes = [];
        this.undoBatch(this.t('붙여넣기'), () => {
            this.ds.batch(() => {
                for (let i = 0; i < rows.length && nirRow < 0; i++) {
                    let vi = start.vi + i;
                    if (vi >= this.disp.length) {
                        if (!this.opts.appendRowOnTab)
                            break;
                        const nv = this.beforeRowInsert(this.view.length, this.opts.newRow?.() ?? {}, 'paste');
                        if (!nv)
                            break;
                        this.ds.addRow(nv);
                    }
                }
            });
            for (let i = 0; i < rows.length; i++) {
                const row = nirRow >= 0 ? nirRow : this.disp[start.vi + i];
                if (row == null || row < 0)
                    continue;
                let ci = start.ci;
                for (const cellText of rows[i]) {
                    while (ci < this.cols.length && this.cols[ci].special)
                        ci++;
                    const c = this.cols[ci++];
                    if (!c)
                        break;
                    const kind = this.editorOf(c, row);
                    if (!kind || !this.canEditCol(c, row))
                        continue; // fillRange 와 같은 가드
                    const p = kind === 'check3' && cellText.trim() === String(this.checkValues3(c)[2]) ? { value: this.checkValues3(c)[2] } : CHECKISH.has(kind) ? { value: /^(1|y|true|v|o|✓)$/i.test(cellText.trim()) ? this.checkValues(c)[0] : this.checkValues(c)[1] }
                        : kind === 'combo' ? this.parseCombo(c, cellText) : this.parseInput(c, cellText, kind, row);
                    if ('error' in p) {
                        errors++;
                        continue;
                    }
                    changes.push({ row, col: c.id, value: p.value });
                }
            }
            this.setCellByUser(changes, this.t('붙여넣기'), 'paste');
        });
        if (errors) {
            this.showTip(`${errors}개 셀은 형식이 맞지 않아 건너뛰었습니다`);
            setTimeout(() => this.hideTip(), 2500);
        }
    }
    /** @internal */ parseCombo(c, text) {
        const t = text.trim();
        if (!t)
            return { value: null };
        const q = t.toLowerCase();
        const hit = this.itemsOf(c).find(i => i.value.toLowerCase() === q || i.label.toLowerCase() === q);
        return hit ? { value: hit.value } : c.def.listStyle === 'editList' ? { value: c.def.upper ? t.toUpperCase() : t } : { error: 'x' };
    }
}
